import ReactECharts from 'echarts-for-react';
import type { StyleConfig } from '../../../../types/bi';
import { useEChartsTheme } from '../../hooks/useEChartsTheme';
import { buildChartClickHandler } from '../../utils/drilldown';
import type { DrilldownEvent } from '../../utils/drilldown';

interface TreeMapProps {
  data: { columns: string[]; rows: Record<string, unknown>[] };
  dimensions: string[];
  measures: { alias: string }[];
  style?: StyleConfig;
  onDrillDown?: (event: DrilldownEvent) => void;
}

export default function TreeMap({ data, dimensions, measures, style, onDrillDown }: TreeMapProps) {
  const theme = useEChartsTheme();

  const measureKey = measures[0]?.alias || data.columns[data.columns.length - 1];

  // Build hierarchical data from dimension columns
  // Single dimension: flat list of rectangles
  // Two dimensions: parent → children
  if (dimensions.length >= 2) {
    const parentDim = dimensions[0];
    const childDim = dimensions[1];
    const grouped = new Map<string, { name: string; value: number }[]>();
    for (const row of data.rows) {
      const parent = String(row[parentDim] ?? '');
      const child = String(row[childDim] ?? '');
      const val = Number(row[measureKey]) || 0;
      if (!grouped.has(parent)) grouped.set(parent, []);
      grouped.get(parent)!.push({ name: child, value: val });
    }
    const treeData = Array.from(grouped.entries()).map(([name, children]) => ({
      name,
      children,
    }));

    const option = {
      tooltip: {
        formatter: (params: { name: string; value: number; treePathInfo: { name: string }[] }) => {
          const path = params.treePathInfo.map((n) => n.name).join(' / ');
          return `${path}<br/>${measureKey}: ${params.value}`;
        },
      },
      series: [
        {
          type: 'treemap' as const,
          data: treeData,
          roam: false,
          label: { show: true, formatter: '{b}' },
          breadcrumb: { show: true },
          levels: [
            {
              itemStyle: {
                borderWidth: 2,
                borderColor: 'var(--border-primary, #ccc)',
                gapWidth: 2,
              },
            },
            {
              itemStyle: { borderWidth: 1, gapWidth: 1 },
              colorSaturation: [0.35, 0.6],
            },
          ],
        },
      ],
    };

    const events = buildChartClickHandler(dimensions, onDrillDown);

    return (
      <ReactECharts
        option={option}
        theme={theme}
        style={{ height: '100%', width: '100%' }}
        onEvents={events}
      />
    );
  }

  // Flat treemap — single dimension
  const dimKey = dimensions[0] || data.columns[0];
  const flatData = data.rows.map((r, i) => ({
    name: String(r[dimKey] ?? ''),
    value: Number(r[measureKey]) || 0,
    itemStyle: style?.colours?.[i] ? { color: style.colours[i] } : undefined,
  }));

  const option = {
    tooltip: {
      formatter: (params: { name: string; value: number }) =>
        `${params.name}<br/>${measureKey}: ${params.value}`,
    },
    series: [
      {
        type: 'treemap' as const,
        data: flatData,
        roam: false,
        label: { show: true, formatter: '{b}\n{c}' },
        breadcrumb: { show: false },
      },
    ],
  };

  return <ReactECharts option={option} theme={theme} style={{ height: '100%', width: '100%' }} />;
}
